import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StructuredResultItem } from '../types';
import { formatDistance } from '../utils';

interface BikeTrailResultItemRendererProps {
  item: StructuredResultItem;
}

export const BikeTrailResultItemRenderer: React.FC<BikeTrailResultItemRendererProps> = ({ item }) => {
  const { t } = useTranslation();

  const getBikeTrailStats = () => {
    const stats = [];
    
    if (item.distance_km) stats.push({
      content: `🚴 ${formatDistance(item.distance_km)} km`,
      testId: 'bike-trail-distance'
    });
    if (item.elevation_gain) stats.push({
      content: `⛰️ ${item.elevation_gain} m`,
      testId: 'elevation-gain'
    });
    
    // Common location info
    stats.push({
      content: `📍 ${item.location}`,
      testId: 'location'
    });
    if (item.distance_from_user !== undefined && item.distance_from_user !== null) {
      stats.push({
        content: `📏 ${formatDistance(item.distance_from_user)} km ${t('results.distance.away')}`,
        testId: 'distance-from-user'
      });
    }
    
    return stats;
  };

  const getDifficultyBadge = () => {
    if (!item.difficulty_level) return null;
    
    const difficultyColors = {
      easy: 'bg-green-100 text-green-800 border-green-200',
      moderate: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      challenging: 'bg-orange-100 text-orange-800 border-orange-200',
      expert: 'bg-red-100 text-red-800 border-red-200'
    };
    
    const color = difficultyColors[item.difficulty_level as keyof typeof difficultyColors] || 'bg-gray-100 text-gray-800 border-gray-200';
    
    return { text: item.difficulty_level, color };
  };

  const badge = getDifficultyBadge();

  return (
    <article className="border rounded p-3 bg-green-50 text-sm border-green-100" data-testid="bike-trail-item">
      {/* Title and Badge */}
      <div className="flex items-start justify-between mb-2">
        <h5 className="font-semibold text-gray-900 leading-tight flex-1 pr-2" data-testid="item-title">
          {item.title}
        </h5>
        {badge && (
          <span 
            className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 border ${badge.color}`}
            data-testid="difficulty-badge"
            role="status"
            aria-label={`Difficulty: ${badge.text}`}
          >
            {badge.text}
          </span>
        )}
      </div>
      
      {/* Bike-specific stats */}
      <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600" data-testid="trail-stats">
        {getBikeTrailStats().map((stat, index) => (
          <span key={index} data-testid={stat.testId}>
            {stat.content}
          </span>
        ))}
      </div>
      
      {/* Description */}
      <p className="text-gray-700 mb-2 text-sm leading-tight" data-testid="item-description">
        {item.description}
      </p>
      
      {/* Link */}
      <a 
        href={item.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center text-green-600 hover:text-green-800 text-xs font-medium"
        data-testid="visit-website-link"
        aria-label={`Visit website for ${item.title}`}
      >
        🔗 {t('results.actions.visitWebsite')}
      </a>
    </article>
  );
};